import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Field, PrimaryButton, SecondaryButton, StepAlert, ToggleRow } from "../ui";
import { calibrationError, calibrationPayload, numberOrNull } from "../calibration";
import {
  deviceTestLabel, formatCountdown, reportedNumber, reportedText, testSnapshotKey,
} from "../deviceTest";
import useInstallationDeviceTest from "../useInstallationDeviceTest";

function fmtTime(value) {
  if (!value) return "Not reported";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleTimeString() : "Not reported";
}

function Line({ label, value, t }) {
  return (
    <Typography variant="body2" sx={{ color: t.textStrong }}>
      <Box component="span" sx={{ color: t.textMuted }}>{label}: </Box>
      {value}
    </Typography>
  );
}

function signalText(signal) {
  const value = reportedNumber(signal?.value);
  if (value === null) return "Not reported";
  return [value, signal.unit, signal.metric ? String(signal.metric).toUpperCase() : ""].filter(Boolean).join(" ");
}

/**
 * Optional "Test device & check reading" panel on the calibration step.
 * Testing is opt-in: nothing is requested, listened to or counted down until
 * the installer selects Start listening, and no outcome blocks setup.
 * Mounted only when the server advertises installation_device_test.
 */
export default function DeviceTestPanel({
  session, deviceId, socket, calibration, tankGeometry, record, dispatch, onContinue, canContinue, t, darkMode,
}) {
  const test = useInstallationDeviceTest({ session, deviceId, socket });
  const [knownHeight, setKnownHeight] = useState("");
  const startedKey = useRef(null);
  const payload = calibration?.enabled && !calibrationError(calibration) ? calibrationPayload(calibration) : null;
  const snapshotKey = testSnapshotKey(payload, tankGeometry);
  const busy = test.starting || test.waiting;

  // Keep the latest values for the unmount cleanup below.
  const live = useRef({ test, dispatch });
  live.current = { test, dispatch };

  // Mirror the fetched result into wizard state for review. A new test
  // always starts unconfirmed; "received" never confirms on its own.
  useEffect(() => {
    const result = test.result;
    if (!result) return;
    dispatch({
      type: "SET_DEVICE_TEST",
      record: {
        test_id: result.test_id,
        status: result.status,
        snapshot_key: startedKey.current,
        received_at: result.observation?.received_at || null,
        measurement_status: result.observation?.measurement_status || null,
      },
    });
    // Only a new test or a status change is worth recording.
  }, [test.result?.test_id, test.result?.status, dispatch]);

  // Changing calibration or accepted geometry stops an active test; the
  // reducer separately marks any retained result stale and unconfirmed.
  useEffect(() => {
    if (startedKey.current !== null && startedKey.current !== snapshotKey && live.current.test.waiting) {
      live.current.test.cancel();
    }
  }, [snapshotKey]);

  // Leaving the step while waiting: stop local work immediately and ask the
  // server to cancel without holding navigation for its answer.
  useEffect(() => () => {
    if (live.current.test.waiting || live.current.test.starting) {
      live.current.test.cancel();
      live.current.dispatch({ type: "SET_DEVICE_TEST_STATUS", status: "cancelled" });
    }
  }, []);

  const begin = () => {
    if (!payload) return;
    startedKey.current = snapshotKey;
    setKnownHeight("");
    test.start({ calibration: payload, geometry: tankGeometry || null });
  };

  const result = test.result;
  const observation = result?.status === "received" ? result.observation : null;
  const stale = Boolean(record?.stale) || (startedKey.current !== null && startedKey.current !== snapshotKey);
  const validMeasurement = observation?.measurement_status === "valid";
  const preview = reportedNumber(observation?.preview_liquid_height_in);
  const known = numberOrNull(knownHeight);
  const timedOut = result?.status === "timed_out" || (!busy && !result && test.error && startedKey.current !== null);

  return (
    <Box mt={3} p={2} borderRadius="12px" sx={{ border: `1px solid ${t.borderSubtle || t.divider}` }}>
      <Typography variant="subtitle2" sx={{ color: t.textStrong }}>
        Test device &amp; check reading (optional)
      </Typography>
      <Typography variant="body2" sx={{ color: t.textMuted }} mb={2}>
        You can finish setup without testing the device now.
      </Typography>

      {!busy && !result ? (
        <Box component="ol" sx={{ pl: 2.5, mt: 0, color: t.textStrong }}>
          <li><Typography variant="body2">Check the sensor is mounted and powered.</Typography></li>
          <li><Typography variant="body2">Select Start listening.</Typography></li>
          <li>
            <Typography variant="body2">
              Use this model&apos;s report/test button to send a reading. It may take a minute or two to reach Rivio.
            </Typography>
          </li>
        </Box>
      ) : null}

      {!payload ? (
        <StepAlert severity="info">Complete the calibration measurements above before testing.</StepAlert>
      ) : null}

      {busy ? (
        <Box role="status" aria-live="polite" mb={2}>
          <Typography fontWeight={700} sx={{ color: t.textStrong }}>
            {test.starting ? "Starting the test..." : "Waiting for a fresh device report..."}
          </Typography>
          <Typography variant="body2" sx={{ color: t.textMuted }} aria-live="off">
            {formatCountdown(test.remaining)} remaining
          </Typography>
          <Line t={t} label="Browser connection to Rivio"
            value={socket?.connected ? "connected" : "not connected - still checking every few seconds"} />
          <Line t={t} label="Device report" value="not received yet" />
        </Box>
      ) : null}

      {test.error ? <StepAlert severity={timedOut ? "warning" : "error"}>{test.error}</StepAlert> : null}

      {result?.status === "failed" ? (
        <StepAlert severity="error">{result.error?.message || "The device test failed. No verified reading is available."}</StepAlert>
      ) : null}
      {result?.status === "cancelled" ? (
        <StepAlert severity="info">Test cancelled. The reading was not verified.</StepAlert>
      ) : null}

      {observation ? (
        <Box role="status" aria-live="polite" mb={2}>
          <Typography fontWeight={700} sx={{ color: t.textStrong }} mb={0.5}>New device report received</Typography>
          <Line t={t} label="Received by Rivio" value={fmtTime(observation.received_at)} />
          <Line t={t} label="Reported by device"
            value={observation.device_reported_at ? fmtTime(observation.device_reported_at) : "Not reported (sample time unverified)"} />
          <Line t={t} label="Connectivity" value="confirmed - a new uplink reached Rivio" />
          {validMeasurement ? (
            <>
              <Line t={t} label="Sensor-to-liquid distance" value={reportedText(observation.sensor_distance_in, "in")} />
              <Line t={t} label="Liquid height using YOUR proposed settings" value={reportedText(observation.preview_liquid_height_in, "in")} />
              <Line t={t} label="Proposed full point" value={reportedText(payload?.scale_max_eng, "in")} />
              <Line t={t} label="Calculated level"
                value={reportedNumber(observation.preview_percent) === null ? "Not reported" : `${Math.round(reportedNumber(observation.preview_percent))}%`} />
            </>
          ) : (
            <StepAlert severity="warning">
              The report reached Rivio but did not contain a usable measurement
              ({observation.measurement_status || "status not reported"}). Connectivity is confirmed; the reading is not.
            </StepAlert>
          )}
          <Line t={t} label="Battery voltage" value={reportedText(observation.battery_voltage, "V", 2)} />
          <Line t={t} label="Signal" value={signalText(observation.signal)} />

          {validMeasurement && preview !== null ? (
            <Box mt={2}>
              <Field label="Known liquid height (in, optional)" type="number" value={knownHeight}
                onChange={(event) => setKnownHeight(event.target.value)}
                inputProps={{ min: 0, step: "any" }}
                helperText="An independently checked height, for comparison only. Not saved."
                t={t} darkMode={darkMode} />
              {known !== null ? (
                <Typography variant="body2" sx={{ color: t.textStrong }} mt={1}>
                  Difference from preview: {(preview - known) >= 0 ? "+" : ""}{(preview - known).toFixed(1)} in.
                  Decide whether this is acceptable for this model; no pass/fail tolerance is applied.
                </Typography>
              ) : null}
            </Box>
          ) : null}

          {stale ? (
            <StepAlert severity="warning">
              Settings changed after this test. Its preview no longer matches; start a new test if you want a verified reading.
            </StepAlert>
          ) : validMeasurement ? (
            <ToggleRow
              label="This matches the tank conditions I checked."
              checked={Boolean(record?.measurement_confirmed)}
              onChange={(confirmed) => dispatch({ type: "CONFIRM_DEVICE_TEST", confirmed })}
            />
          ) : null}
          <Typography variant="caption" display="block" sx={{ color: t.textMuted }}>
            This previews your proposed calibration. It is not saved until installation.
          </Typography>
        </Box>
      ) : null}

      {!busy && record && !result ? (
        <Typography variant="body2" sx={{ color: t.textMuted }} mb={2}>
          Last test: {deviceTestLabel(record)}
        </Typography>
      ) : null}

      <Box display="grid" gap={1.5}>
        {busy ? (
          <>
            <SecondaryButton t={t} onClick={() => test.cancel()}>Cancel test</SecondaryButton>
            <PrimaryButton t={t} disabled={!canContinue} onClick={() => {
              test.cancel();
              onContinue();
            }}>
              Stop test and continue setup
            </PrimaryButton>
          </>
        ) : (
          <>
            <PrimaryButton t={t} disabled={!payload || test.starting} onClick={begin}>
              {result || startedKey.current !== null ? "Retry test" : "Start listening"}
            </PrimaryButton>
            {timedOut ? (
              <SecondaryButton t={t} disabled={test.checking} onClick={() => test.checkStored()}>
                {test.checking ? "Checking..." : "Check stored test result"}
              </SecondaryButton>
            ) : null}
            <SecondaryButton t={t} disabled={!canContinue} onClick={onContinue}>
              {timedOut ? "Continue without verified reading" : "Continue without testing"}
            </SecondaryButton>
          </>
        )}
      </Box>
    </Box>
  );
}
